/**
 * Loan persistence (FR-LOAN-01 to FR-LOAN-11).
 *
 * The schedule is written once from the lender's PDF and then only read. Nothing here
 * recomputes it — FR-LOAN-04 makes the lender's numbers authoritative, and an
 * "improvement" that re-derived them would quietly disagree with the loan statement by a
 * rupee or two per row.
 *
 * **Which instalments are paid is derived, never stored.** A transaction carrying
 * `loanId` and `instalmentNo` *is* the payment; a `paid` flag on the schedule would be a
 * second copy of that fact, free to drift from it. This is the same reasoning as
 * FR-RCV-02's derived outstanding.
 */

import {
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { DateKey } from '../core/dates';
import {
  MAHINDRA_CAR_LOAN,
  MAHINDRA_SCHEDULE,
  type Instalment,
  type Loan,
} from '../core/loans';
import { auditUpdate } from './audit';
import { db, getStorageLazy } from './firebase';
import { trackWrite } from './syncState';
import { toTransactionList } from './transactions';

const LOANS = 'loans';

export class LoanDataError extends Error {}

function toLoan(id: string, data: Record<string, unknown>): Loan {
  return { ...(data as Omit<Loan, 'id'>), id };
}

export function watchLoans(onChange: (loans: Loan[]) => void): () => void {
  return onSnapshot(
    collection(db, LOANS),
    (snapshot) => onChange(snapshot.docs.map((d) => toLoan(d.id, d.data()))),
    () => onChange([]),
  );
}

export function watchSchedule(
  loanId: string,
  onChange: (schedule: Instalment[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, LOANS, loanId, 'schedule'), orderBy('instalmentNo')),
    (snapshot) => onChange(snapshot.docs.map((d) => d.data() as Instalment)),
    () => onChange([]),
  );
}

/**
 * Seed the one loan and its 60 instalments (FR-LOAN-02, FR-LOAN-03).
 *
 * Refuses to run twice, like `seedAccounts`: a "seed" button is exactly the sort of thing
 * that gets clicked again, and re-running it over a schedule whose instalments have since
 * been corrected would silently undo those corrections.
 */
export async function seedLoan(by: string): Promise<number> {
  const existing = await getDocs(collection(db, LOANS));
  if (!existing.empty) {
    throw new LoanDataError(`Loans already exist (${existing.size}). Seeding would overwrite them.`);
  }

  const { id, ...loan } = MAHINDRA_CAR_LOAN;
  await trackWrite(
    setDoc(doc(db, LOANS, id), {
      ...loan,
      documents: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    }),
  );

  // 60 instalments, well inside one batch's 500 operations, but chunked anyway so a
  // longer tenure on a future loan does not quietly hit the limit.
  const CHUNK = 200;
  for (let i = 0; i < MAHINDRA_SCHEDULE.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const instalment of MAHINDRA_SCHEDULE.slice(i, i + CHUNK)) {
      batch.set(
        doc(db, LOANS, id, 'schedule', String(instalment.instalmentNo).padStart(3, '0')),
        instalment,
      );
    }
    await trackWrite(batch.commit());
  }

  return MAHINDRA_SCHEDULE.length;
}

export interface PaidInstalment {
  instalmentNo: number;
  transactionId: string;
  dateKey: DateKey;
}

/**
 * Which instalments have been paid, read from the transactions themselves.
 *
 * One query on `loanId`, which is a single-field index Firestore maintains automatically.
 */
export async function paidInstalments(loanId: string): Promise<PaidInstalment[]> {
  const snapshot = await getDocs(
    query(collection(db, 'transactions'), where('loanId', '==', loanId), where('deleted', '==', false)),
  );

  return toTransactionList(snapshot.docs)
    .filter((t) => t.instalmentNo !== null)
    .map((t) => ({ instalmentNo: t.instalmentNo!, transactionId: t.id, dateKey: t.dateKey }))
    .sort((a, b) => a.instalmentNo - b.instalmentNo);
}

export async function setLoanArchived(loan: Loan, archived: boolean, by: string): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, LOANS, loan.id), { archived, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(LOANS, loan.id, { archived: loan.archived }, { archived }, by);
}

// ---------------------------------------------------------------------------
// Documents (FR-LOAN-09, AD-10)
// ---------------------------------------------------------------------------

export interface LoanDocument {
  path: string;
  name: string;
  sizeBytes: number;
  contentType: string;
  kind: 'schedule' | 'kfs' | 'other';
}

/**
 * Upload a loan document.
 *
 * Stored under `loan-docs/`, where the Storage rules **deny client read outright** — not
 * "the UI does not link to it", the rule refuses (AD-10). These files carry PAN, Aadhaar
 * references, DOB, address, income and co-applicant details.
 *
 * The consequence, and it is a real one: **until the Cloud Run signed-URL route arrives
 * at M9, an uploaded document can be listed but not opened in the app.** That is the
 * honest state of it. Weakening the rule to make the link work today would undo the one
 * thing the separate path exists for.
 */
export async function uploadLoanDocument(
  loanId: string,
  file: File,
  kind: LoanDocument['kind'],
): Promise<LoanDocument> {
  if (file.size > 10 * 1024 * 1024) {
    throw new LoanDataError('That file is over 10 MB. The storage rules reject it.');
  }
  if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
    throw new LoanDataError('Loan documents must be a PDF or an image.');
  }

  const { ref, uploadBytes } = await import('firebase/storage');
  const storage = await getStorageLazy();

  // Timestamped rather than overwritten in place: a re-issued schedule is a new document,
  // and the old one is what past quotes were computed from.
  const path = `loan-docs/${loanId}/${Date.now()}-${safeName(file.name)}`;
  await trackWrite(uploadBytes(ref(storage, path), file, { contentType: file.type }));

  const document: LoanDocument = {
    path,
    name: file.name,
    sizeBytes: file.size,
    contentType: file.type,
    kind,
  };

  const { arrayUnion } = await import('firebase/firestore');
  await trackWrite(
    updateDoc(doc(db, LOANS, loanId), {
      documents: arrayUnion(document),
      updatedAt: serverTimestamp(),
    }),
  );

  return document;
}

/** Storage object names: keep it printable and free of path separators. */
function safeName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]+/g, '-').slice(0, 80) || 'document';
}

/** Clears a field left over from an earlier shape. Exported for the migration path. */
export async function clearLoanField(loanId: string, field: string): Promise<void> {
  await trackWrite(updateDoc(doc(db, LOANS, loanId), { [field]: deleteField() }));
}
